import { useEffect, useMemo, useState } from "react";
import { Alert, Nav, Tab } from "react-bootstrap";

import { formatPageLabel } from "../utils/content.utils";
import PageEditor from "./PageEditor";
import ThemeEditor from "./ThemeEditor";

const ContentForm = ({
  content,
  validationErrors,
  submitting,
  uploading,
  readOnly,
  submitLabel = "Update Content",
  submittingLabel = "Updating...",
  onSubmit,
  onContentChange,
  onUploadStart,
  onUploadEnd,
  onBack,
}) => {
  const pages = useMemo(
    () => (Array.isArray(content?.pages) ? content.pages : []),
    [content?.pages]
  );
  const [activePage, setActivePage] = useState("");

  useEffect(() => {
    if (!pages.length) {
      setActivePage("");
      return;
    }
    const ids = pages.map((page) => page?.id).filter(Boolean);
    if (!activePage || !ids.includes(activePage)) {
      setActivePage(ids[0] || "");
    }
  }, [pages, activePage]);

  const disabled = submitting || uploading || readOnly;

  const updateTheme = (theme) => {
    onContentChange({ ...content, theme });
  };

  const updatePage = (index, nextPage) => {
    const nextPages = pages.map((page, i) => (i === index ? nextPage : page));
    onContentChange({ ...content, pages: nextPages });
  };

  return (
    <form onSubmit={onSubmit}>
      {validationErrors?.content ? (
        <Alert variant="danger" className="mb-3">
          {validationErrors.content}
        </Alert>
      ) : null}

      <ThemeEditor
        theme={content?.theme}
        disabled={disabled}
        onChange={updateTheme}
      />

      <hr className="my-4" />

      <h5 className="mb-3">Pages</h5>

      {pages.length === 0 ? (
        <p className="text-muted">No pages in this content document.</p>
      ) : (
        <Tab.Container
          activeKey={activePage || pages[0]?.id}
          onSelect={(key) => key && setActivePage(key)}
        >
          <Nav variant="tabs" className="flex-wrap mb-3">
            {pages.map((page, index) => {
              const pageId = page?.id || `page-${index}`;
              return (
                <Nav.Item key={pageId}>
                  <Nav.Link eventKey={pageId}>
                    {formatPageLabel(page?.id || pageId)}
                  </Nav.Link>
                </Nav.Item>
              );
            })}
          </Nav>

          <Tab.Content>
            {pages.map((page, index) => {
              const pageId = page?.id || `page-${index}`;
              return (
                <Tab.Pane eventKey={pageId} key={pageId}>
                  <PageEditor
                    page={page}
                    disabled={disabled}
                    onChange={(next) => updatePage(index, next)}
                    onUploadStart={onUploadStart}
                    onUploadEnd={onUploadEnd}
                  />
                </Tab.Pane>
              );
            })}
          </Tab.Content>
        </Tab.Container>
      )}

      <div
        className="d-flex gap-2 pt-3 mt-4 border-top sticky-bottom bg-white"
        style={{ bottom: 0, zIndex: 2, paddingBottom: 8 }}
      >
        <button
          type="submit"
          className="btn btn-primary"
          disabled={readOnly || submitting || uploading}
        >
          {submitting
            ? submittingLabel
            : uploading
              ? "Uploading image..."
              : submitLabel}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onBack}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ContentForm;
